import { DELEGON_URL } from 'configuration';
import i18n from 'i18n';
import { DEFAULT_LOCALE, SUPPORTED_LOCALES } from 'i18n/config';

//  EXTERNAL LINKS
export const EXTERNAL = {
  TERMS_OF_SERVICE: `${DELEGON_URL}/terms-and-conditions/`,
  PRIVACY_POLICE: `${DELEGON_URL}/privacy-policy/`,
  PRICING_SUPPLIERS: `${DELEGON_URL}/pricing/`,
  PRICING_BUYERS: `${DELEGON_URL}/buyers/pricing/`,
  BLOGS: `${DELEGON_URL}/blogs/`,
  CONTACT_US: `${DELEGON_URL}/contact-us/`,
  HOMEPAGE: `${DELEGON_URL}/`,
};

//  ------------------------------------------------------------

//  INTERNAL LINKS
//  ROOT_PATHS & SUB_PATHS are used for the react-router-dom router objects
export const ROOT_PATHS = {
  AUTH: '/auth',
  HOMEPAGE: 'home/',
  USER_SEARCH: 'user-search/',
  COMPANY_PROFILE: 'company-profile/',
  FRAMEWORK_AGREEMENT: 'framework-agreements/',
  FRAMEWORK_AGREEMENT_DETAILS: 'framework-agreements/:id',
  LOGOUT: 'logout/',
  REQUEST_ACCESS: 'reqeuest-access/',
  MARKETPLACE: 'marketplace/',
  BUYERS: 'buyers/',
  BUYERS_DETAILS: 'buyers/:id',
  BUYERS_USERS: 'buyers/:id/users',
  SUPPLIERS: 'suppliers/',
  SUPPLIERS_DETAILS: 'suppliers/:id',
  SUPPLIERS_USERS: 'suppliers/:id/users',
  SUPPLIER_SEARCH: 'supplier_search/',
  USERS: 'contacts/',
  USER_DETAILS: 'contacts/:id',
  SUBSCRIPTIONS: 'subscriptions/',
  STATISTICS: 'statistics/',
  SETTINGS: 'settings/',
  DIRECTS: 'directs/',
  DIRECTS_DETAILS: 'directs/:id',
  DPS: 'dps/',
  DPS_DETAILS: 'dps/:id',
  ACCESS_REQUESTED: 'access-requested/',
  JOIN_COMPANY: 'join-company/',
  WEBSITE: '/website',
  WEBSITE_BUYERS: '/website/buyers',
  BLOG: 'blog/',
  BLOG_DETAILS: 'blog/:id',
  SUBSCRIBERS: 'subscribers/',
  JOB_ADS: 'job_ads/',
  JOB_ADS_DETAILS: 'job_ads/:id',
  ADMIN_JOB_ADS_DETAILS: 'job_ads/:id/edit',
  MY_JOB_ADS: 'my_ads/',
  MY_JOB_ADS_DETAILS: 'my_ads/:id',
  APPLICATIONS: 'applications/',
  APPLICATION_DETAILS: 'applications/:id',
  NOTIFICATIONS: 'notifications/',
  COMPANY_PUBLIC_PROFILE: '/profile/:type/:slug',
  LOCALIZED_COMPANY_PUBLIC_PROFILE: '/:lang/profile/:type/:slug',
  ROOT: '/',
};

export const SUB_PATHS = {
  auth: {
    login: '/login',
    register: '/register',
    buyersRegister: '/signup',
    verifyEmail: '/verify-email',
    forgotPassword: '/forgot-password',
    passwordReset: '/reset-password',
    action: '/action',
    acceptInvitation: '/accept-invitation',
    accessRequested: '/access-requested',
    confirmationSent: '/confirmation-sent',
  },
  frameworkAgreement: {
    details: ':id',
    consortiumGroup: 'consortium-group',
    consortiumGroupDetails: 'consortium-group/:cgId',
    consortiumGroupFiles: 'consortium-group/:facId/files',
    suppliers: 'suppliers',
    suppliersFiles: 'suppliers/:facId/files',
  },
  directs: {
    details: ':id',
    consortiumGroup: 'consortium-group',
    consortiumGroupDetails: 'consortium-group/:cgId',
    consortiumGroupFiles: 'consortium-group/:tcId/files',
    suppliers: 'suppliers',
    suppliersFiles: 'suppliers/:tcId/files',
  },
  dps: {
    details: ':id',
    // consortiumGroup: 'consortium-group',
    // consortiumGroupDetails: 'consortium-group/:cgId',
    // consortiumGroupFiles: 'consortium-group/:dcId/files',
    // suppliers: 'suppliers',
    // suppliersFiles: 'suppliers/:dcId/files',
  },
  website: {
    termsAndConditions: '/terms-and-conditions',
    contactUs: '/contact-us',
    pricing: '/pricing',
    buyersPricing: '/buyers/pricing',
    blog: '/blog',
    jobAds: '/job_ads',
    plus: '/plus',
  },
  blog: {
    details: ':id',
  },
};

//  ------------------------------------------------------------------------

const makePublicURI = (uriParts) => uriParts.reduce((URI, part) => (URI += part), '');
const makeProtectedURI = (uriParts) => uriParts.reduce((URI, part) => (URI += part), '/app/');
const makeWebsiteURI = (uriParts) => uriParts.reduce((URI, part) => (URI += part), '');

//  Prefixes a website path with the current locale, e.g. /website/pricing -> /en/website/pricing.
//  No-op for paths outside ROOT_PATHS.WEBSITE (e.g. /app/..., /auth/...).
export const withLocale = (path, lang) =>
  path.startsWith(ROOT_PATHS.WEBSITE) ? `/${lang}${path}` : path;

const currentLocale = () =>
  SUPPORTED_LOCALES.includes(i18n.resolvedLanguage) ? i18n.resolvedLanguage : DEFAULT_LOCALE;

//  paths are used for the react-router-dom Link component
export const paths = {
  auth: {
    login: makePublicURI([ROOT_PATHS.AUTH, SUB_PATHS.auth.login]),
    register: makePublicURI([ROOT_PATHS.AUTH, SUB_PATHS.auth.register]),
    buyersRegister: makePublicURI([ROOT_PATHS.AUTH, SUB_PATHS.auth.buyersRegister]),
    verifyEmail: makePublicURI([ROOT_PATHS.AUTH, SUB_PATHS.auth.verifyEmail]),
    forgotPassword: makePublicURI([ROOT_PATHS.AUTH, SUB_PATHS.auth.forgotPassword]),
    passwordReset: makePublicURI([ROOT_PATHS.AUTH, SUB_PATHS.auth.passwordReset]),
    action: makePublicURI([ROOT_PATHS.AUTH, SUB_PATHS.auth.action]),
    acceptInvitation: makePublicURI([ROOT_PATHS.AUTH, SUB_PATHS.auth.acceptInvitation]),
    accessRequested: makePublicURI([ROOT_PATHS.AUTH, SUB_PATHS.auth.accessRequested]),
    confirmationSent: makePublicURI([ROOT_PATHS.AUTH, SUB_PATHS.auth.confirmationSent]),
  },

  homepage: makeProtectedURI([ROOT_PATHS.HOMEPAGE]),
  userSearch: makeProtectedURI([ROOT_PATHS.USER_SEARCH]),
  companyProfile: makeProtectedURI([ROOT_PATHS.COMPANY_PROFILE]),
  frameworkAgreement: makeProtectedURI([ROOT_PATHS.FRAMEWORK_AGREEMENT]),
  frameworkAgreementDetailsView: (faId) => `/app/${ROOT_PATHS.FRAMEWORK_AGREEMENT + faId}`,
  frameworkAgreementConsortiumGroups: (faId, cgId) =>
    `/app/${ROOT_PATHS.FRAMEWORK_AGREEMENT + faId}/${
      SUB_PATHS.frameworkAgreement.consortiumGroup
    }/${cgId}`,
  frameworkAgreementConsortiumGroupsFiles: (faId, facId) =>
    `/app/${ROOT_PATHS.FRAMEWORK_AGREEMENT + faId}?tab=files&subtab=${facId}`,
  frameworkAgreementSuppliersFiles: (faId, facId) =>
    `/app/${ROOT_PATHS.FRAMEWORK_AGREEMENT + faId}?tab=files&subtab=${facId}`,

  logout: makeProtectedURI([ROOT_PATHS.LOGOUT]),
  requestAccess: makeProtectedURI([ROOT_PATHS.REQUEST_ACCESS]),
  accessRequested: makeProtectedURI([ROOT_PATHS.ACCESS_REQUESTED]),
  joinCompany: makeProtectedURI([ROOT_PATHS.JOIN_COMPANY]),
  marketplace: makeProtectedURI([ROOT_PATHS.MARKETPLACE]),
  buyers: makeProtectedURI([ROOT_PATHS.BUYERS]),
  buyersDetailsView: (id) => makeProtectedURI([ROOT_PATHS.BUYERS, id]),
  buyersUsersView: (id) => makeProtectedURI([ROOT_PATHS.BUYERS, `${id}/users`]),
  suppliers: makeProtectedURI([ROOT_PATHS.SUPPLIERS]),
  suppliersDetailsView: (id) => makeProtectedURI([ROOT_PATHS.SUPPLIERS, id]),
  suppliersUsersView: (id) => makeProtectedURI([ROOT_PATHS.SUPPLIERS, `${id}/users`]),
  supplierSearch: makeProtectedURI([ROOT_PATHS.SUPPLIER_SEARCH]),
  users: makeProtectedURI([ROOT_PATHS.USERS]),
  subscriptions: makeProtectedURI([ROOT_PATHS.SUBSCRIPTIONS]),
  statistics: makeProtectedURI([ROOT_PATHS.STATISTICS]),
  settings: makeProtectedURI([ROOT_PATHS.SETTINGS]),
  subscribers: makeProtectedURI([ROOT_PATHS.SUBSCRIBERS]),

  directs: makeProtectedURI([ROOT_PATHS.DIRECTS]),
  directsDetailsView: (dId) =>
   `/app/${ROOT_PATHS.DIRECTS + dId}`,
  directsConsortiumGroups: (dId, cgId) =>
    `/app/${ROOT_PATHS.DIRECTS + dId}/${
      SUB_PATHS.directs.consortiumGroup
    }/${cgId}`,
  directsConsortiumGroupsFiles: (dId, tcId) =>
    `/app/${ROOT_PATHS.DIRECTS + dId}?tab=files&subtab=${tcId}`,
  directsSuppliersFiles: (dId, tcId) =>
    `/app/${ROOT_PATHS.DIRECTS + dId}?tab=files&subtab=${tcId}`,

  dps: makeProtectedURI([ROOT_PATHS.DPS]),
  dpsDetailsView: (dpsId) =>
   `/app/${ROOT_PATHS.DPS + dpsId}`,
  // dpsConsortiumGroups: (dpsId, cgId) =>
  //   `/app/${ROOT_PATHS.DPS + dpsId}/${
  //     SUB_PATHS.dps.consortiumGroup
  //   }/${cgId}`,
  // dpsConsortiumGroupsFiles: (dpsId, dcId) =>
  //   `/app/${ROOT_PATHS.DPS + dpsId}/${
  //     SUB_PATHS.dps.consortiumGroup
  //   }/${dcId}/files`,
  // dpsSuppliersFiles: (dpsId, dcId) =>
  //   `/app/${ROOT_PATHS.DPS + dpsId}/${
  //     SUB_PATHS.dps.suppliers
  //   }/${dcId}/files`,


  jobAds: makeProtectedURI([ROOT_PATHS.JOB_ADS]),
  jobAdsDetailsView: (id) =>
   `/app/${ROOT_PATHS.JOB_ADS + id}`,
  adminJobAdsDetailsView: (id) =>
   `/app/${ROOT_PATHS.JOB_ADS + id}/edit`,

  myJobAds: makeProtectedURI([ROOT_PATHS.MY_JOB_ADS]),
  myJobAdsDetailsView: (id) =>
   `/app/${ROOT_PATHS.MY_JOB_ADS + id}`,

  applications: makeProtectedURI([ROOT_PATHS.APPLICATIONS]),
  applicationDetailsView: (id) => makeProtectedURI([ROOT_PATHS.APPLICATIONS, id]),

  notifications: makeProtectedURI([ROOT_PATHS.NOTIFICATIONS]),
  notificationTarget: (link) => makeProtectedURI([link]),

  website: {
    termsAndConditions: makeWebsiteURI([ROOT_PATHS.WEBSITE, SUB_PATHS.website.termsAndConditions]),
    contactUs: makeWebsiteURI([ROOT_PATHS.WEBSITE, SUB_PATHS.website.contactUs]),
    pricing: makeWebsiteURI([ROOT_PATHS.WEBSITE, SUB_PATHS.website.pricing]),
    buyersPricing: makeWebsiteURI([ROOT_PATHS.WEBSITE, SUB_PATHS.website.buyersPricing]),
    blog: makeWebsiteURI([ROOT_PATHS.WEBSITE, SUB_PATHS.website.blog]),
    blogDetailsView: (slug) => makeWebsiteURI([ROOT_PATHS.WEBSITE, `${SUB_PATHS.website.blog}/${slug}`]),
    jobAds: makeWebsiteURI([ROOT_PATHS.WEBSITE, SUB_PATHS.website.jobAds]),
    jobAdsDetailsView: (slug) => makeWebsiteURI([ROOT_PATHS.WEBSITE, `${SUB_PATHS.website.jobAds}/${slug}`]),
    plus: makeWebsiteURI([ROOT_PATHS.WEBSITE, SUB_PATHS.website.plus]),
  },

  blog: makeProtectedURI([ROOT_PATHS.BLOG]),
  blogDetailsView: (blogID) => 
    `/app/${ROOT_PATHS.BLOG + blogID}`,

  public: {
    // `lang` defaults to the language currently shown, so links keep the visitor's
    // language (e.g. a blog post opened from a Swedish profile stays Swedish)
    // without every call site having to pass it.
    blogDetailsView: (slug, lang = currentLocale()) =>
      makePublicURI([`/${lang}/website/${ROOT_PATHS.BLOG}`, slug]),
    jobAdsDetailsView: (slug, lang = currentLocale()) =>
      makePublicURI([`/${lang}/website/job_ad/`, slug]),
    // companyDetailsViewBySlug: (type, slug) => 
    //   makePublicURI([`/profile/${type}/`, slug]),
    companyPublicProfile: (type, slug, lang = currentLocale()) =>
      makePublicURI([`/${lang}/profile/${type}/`, slug]),
    unsubscribe: (token) =>
      makePublicURI([`/website/unsubscribe/?token=`, token]),
  }
};
